import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Delete, Check, GripHorizontal, EyeOff } from "lucide-react";

export const NUMPAD_INITIAL = { right: 24, top: 80 };
const PANEL_W = 500; // Ιούν 2026: 500×500, πάνω από τα μόνιμα είδη
const PANEL_H = 500;

/**
 * Floating numeric keypad.
 *
 * Props:
 *   open        boolean
 *   label       string — what's being edited (header). Empty hides the title.
 *   value       string — current value (controlled)
 *   onChange    (newValue)
 *   onAccept    (parsedFloat | null)
 *   onClose     ()
 *   allowDot    show comma/decimal key
 */
export default function Numpad({
  open,
  label = "",
  value,
  onChange,
  onAccept,
  onClose,
  allowDot = true,
}) {
  const [pos, setPos] = useState(null);
  const dragRef = useRef({ dragging: false, offX: 0, offY: 0 });
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Ιούν 2026: σταθερό σημείο — οριζόντια στο κέντρο του τμήματος των
    // ειδών, με το κάτω μέρος ακριβώς πάνω από τη σειρά μόνιμων ειδών,
    // ώστε να μένουν ορατές τουλάχιστον 2 σειρές ειδών από πάνω.
    const anchor = document.querySelector('[data-testid="products-area"]');
    const favs = document.querySelector('[data-testid$="-favorites-row"]');
    let left = Math.max(8, (window.innerWidth - PANEL_W) / 2);
    let top = window.innerHeight / 2 - PANEL_H / 2;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      left = Math.max(8, r.left + r.width / 2 - PANEL_W / 2);
      const bottomEdge = favs ? favs.getBoundingClientRect().top : r.bottom;
      top = bottomEdge - PANEL_H - 4;
    }
    top = Math.max(8, Math.min(top, window.innerHeight - PANEL_H - 8));
    setPos({ left, top });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMouseMove = (e) => {
      if (!dragRef.current.dragging) return;
      setPos({
        left: e.clientX - dragRef.current.offX,
        top: Math.max(0, e.clientY - dragRef.current.offY),
      });
    };
    const onMouseUp = () => {
      dragRef.current.dragging = false;
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [open]);

  if (!open) return null;

  const style = pos
    ? { left: pos.left, top: pos.top, right: "auto" }
    : { left: "50%", transform: "translateX(-50%)", top: 120 };

  const press = (k) => {
    let v = String(value ?? "");
    if (k === "back") v = v.slice(0, -1);
    else if (k === "clear") v = "";
    else if (k === ",") {
      // act as decimal separator (store as ".")
      if (!v.includes(".")) v += v.length ? "." : "0.";
    } else {
      v += k;
    }
    onChange?.(v);
  };

  const accept = () => {
    const parsed = parseFloat(String(value).replace(",", "."));
    onAccept?.(isNaN(parsed) ? null : parsed);
  };

  const onMouseDown = (e) => {
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = {
      dragging: true,
      offX: e.clientX - rect.left,
      offY: e.clientY - rect.top,
    };
    e.preventDefault();
  };

  // Main keypad rows (3x4) — last row: comma, 0, backspace
  const keys = ["7", "8", "9", "4", "5", "6", "1", "2", "3", allowDot ? "," : "", "0", "back"];

  // Feb 2026 — render into document.body via portal.
  // Why: when the Numpad lives inside a Radix DialogContent (which sets
  // ``transform: translate(-50%, -50%)``), any ``position: fixed``
  // descendant is anchored to the transformed ancestor, NOT the
  // viewport — so ``top: 80; right: 24`` was ending up inside the
  // dialog box, and Radix ``onInteractOutside`` closed the dialog when
  // the operator tapped a key. Rendering to ``document.body`` restores
  // viewport-relative fixed positioning. Force-Reset (and any other
  // caller) additionally suppresses ``onInteractOutside`` when the
  // target has ``data-numpad-panel`` (see Reports.jsx).
  return createPortal(
    <div
      ref={panelRef}
      style={style}
      className="fixed z-[60] w-[500px] h-[500px] flex flex-col overflow-hidden bg-white border-2 border-slate-300 rounded-xl shadow-2xl select-none"
      data-testid="numpad-panel"
      data-numpad-panel="true"
    >
      {/* Header — drag handle (no Hide button here anymore) */}
      <div
        onMouseDown={onMouseDown}
        className="cursor-grab active:cursor-grabbing flex items-center justify-between bg-slate-100 border-b-2 border-slate-300 rounded-t-xl px-3 py-2"
      >
        <div className="flex items-center gap-2 text-slate-700">
          <GripHorizontal size={18} />
          {label && (
            <span className="font-bold text-sm tracking-wider">
              {label}
            </span>
          )}
        </div>
      </div>

      {/* Display */}
      <div
        className="px-4 py-1 bg-slate-900 text-white text-3xl font-mono tabular text-right min-h-[48px] flex items-center justify-end shrink-0"
        data-testid="numpad-display"
      >
        {value || <span className="text-slate-500">0</span>}
      </div>

      {/* Keypad */}
      <div className="flex-1 min-h-0 grid grid-cols-3 gap-1.5 p-2">
        {keys.map((k, i) =>
          k === "" ? (
            <div key={i} />
          ) : (
            <button
              key={i}
              onClick={() => press(k)}
              data-testid={`numpad-key-${k}`}
              className="h-full rounded-lg bg-white border-2 border-slate-200 text-2xl font-bold font-mono hover:bg-slate-50 active:bg-slate-100 active:scale-[0.96] transition-all"
            >
              {k === "back" ? <Delete size={24} className="mx-auto" /> : k}
            </button>
          )
        )}
      </div>

      {/* Bottom actions: Απόκρυψη (left) — C (middle, red) — OK (right) */}
      <div className="grid grid-cols-3 gap-1.5 px-2 pb-2 shrink-0">
        <button
          onClick={onClose}
          data-testid="numpad-hide-btn"
          className="h-14 rounded-lg bg-slate-700 border-2 border-slate-800 text-white font-bold hover:bg-slate-800 active:scale-[0.96] transition-all flex items-center justify-center gap-1.5 text-sm"
        >
          <EyeOff size={16} />
          Απόκρυψη
        </button>
        <button
          onClick={() => press("clear")}
          data-testid="numpad-clear"
          className="h-14 rounded-lg bg-red-600 border-2 border-red-700 text-white text-xl font-extrabold hover:bg-red-700 active:scale-[0.96] transition-all"
        >
          C
        </button>
        <button
          onClick={accept}
          data-testid="numpad-accept"
          className="h-14 rounded-lg bg-emerald-600 border-2 border-emerald-700 text-white font-bold hover:bg-emerald-700 active:scale-[0.96] transition-all flex items-center justify-center gap-1.5"
        >
          <Check size={18} />
          OK
        </button>
      </div>
    </div>,
    document.body,
  );
}
